import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KioskEventReviewItem, KioskEventReviewList, hasMessage } from '@cloudsign/shared';
import type { KioskEventReviewStatus } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useSite } from '../../lib/site';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, CheckboxField, EmptyState, FilterBar, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';

const PAGE_SIZE = 20;
const STATUSES: KioskEventReviewStatus[] = ['rejected', 'pending', 'validation_warning'];
const kioskEventsKey = ['devices', 'kiosk-events'] as const;

function StatusBadge({ status }: { status: KioskEventReviewStatus }) {
  return <Badge tone={status === 'rejected' ? 'bad' : 'warn'}>{t(`w23.kioskEvents.status.${status}`)}</Badge>;
}

function Row({ item, siteName, onReviewed }: { item: KioskEventReviewItem; siteName: string; onReviewed: () => void }) {
  const toast = useToast();
  const mark = useMutation({
    mutationFn: () => api.post(`/visits/kiosk-events/${item.idempotencyKey}/reviewed`, { schema: KioskEventReviewItem }),
    onSuccess: () => {
      toast(t('visit.kiosk_event.reviewed'));
      onReviewed();
    },
  });
  return (
    <tr>
      <td className="px-4 py-2.5 text-slate-700">{formatDateTime(item.receivedAt)}</td>
      <td className="px-4 py-2.5 text-slate-700">
        {item.deviceName ?? '-'}
        {siteName ? <span className="block text-xs text-slate-600">{siteName}</span> : null}
      </td>
      <td className="px-4 py-2.5 text-slate-700">{t(`w23.kioskEvents.type.${item.type}`)}</td>
      <td className="px-4 py-2.5">
        <StatusBadge status={item.status} />
        {item.messageKey ? <span className="mt-1 block text-xs text-slate-600">{hasMessage(item.messageKey) ? t(item.messageKey) : item.messageKey}</span> : null}
      </td>
      <td className="px-4 py-2.5 text-slate-700">
        {item.reviewedAt ? t('w23.kioskEvents.reviewedBy', { who: item.reviewedByLabel ?? '', when: formatDateTime(item.reviewedAt) }) : <Badge>{t('w23.kioskEvents.unreviewed')}</Badge>}
      </td>
      <td className="px-4 py-2.5 text-right">
        <span className="inline-flex flex-wrap justify-end gap-2">
          {item.visitId ? (
            <Link className="inline-flex min-h-9 items-center text-sm font-medium text-brand underline" to={`/today/visits/${item.visitId}`}>
              {t('w23.kioskEvents.openVisit')}
            </Link>
          ) : null}
          {!item.reviewedAt ? (
            <Button disabled={mark.isPending} onClick={() => mark.mutate()}>
              {t('w23.kioskEvents.markReviewed')}
            </Button>
          ) : null}
        </span>
        <ErrorLine error={mark.error} />
      </td>
    </tr>
  );
}

/**
 * Kiosk events that need a look: rejected, still pending, or applied with a validation warning (visits-phase3 section 5).
 * Needs devices.view and history.view (the server checks both). No payload contents and no names are shown.
 */
export function KioskEventsTab() {
  const qc = useQueryClient();
  const { sites } = useSite();
  const [siteId, setSiteId] = useState('');
  const [status, setStatus] = useState<'' | KioskEventReviewStatus>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [unreviewedOnly, setUnreviewedOnly] = useState(true);
  const [page, setPage] = useState(1);
  const query = { siteId: siteId || undefined, status: status || undefined, from: from || undefined, to: to || undefined, unreviewedOnly, page, pageSize: PAGE_SIZE };
  const q = useQuery({
    queryKey: [...kioskEventsKey, query],
    queryFn: () => api.get('/visits/kiosk-events', { query, schema: KioskEventReviewList }),
    placeholderData: keepPreviousData,
  });
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? '';
  return (
    <>
      <p className="mb-3 text-sm text-slate-700">{t('w23.kioskEvents.intro')}</p>
      <FilterBar columns="sm:grid-cols-5">
        <SelectField
          label={t('devicesUi.filter.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('devicesUi.filter.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('w23.kioskEvents.col.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            setPage(1);
          }}
        >
          <option value="">{t('w23.kioskEvents.status.all')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`w23.kioskEvents.status.${s}`)}
            </option>
          ))}
        </SelectField>
        <TextField
          label={t('w23.kioskEvents.from')}
          type="date"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setPage(1);
          }}
        />
        <TextField
          label={t('w23.kioskEvents.to')}
          type="date"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setPage(1);
          }}
        />
        <CheckboxField
          label={t('w23.kioskEvents.unreviewedOnly')}
          checked={unreviewedOnly}
          onChange={(e) => {
            setUnreviewedOnly(e.target.checked);
            setPage(1);
          }}
        />
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('w23.kioskEvents.title')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('w23.kioskEvents.col.received')}</Th>
                <Th>{t('w23.kioskEvents.col.device')}</Th>
                <Th>{t('w23.kioskEvents.col.type')}</Th>
                <Th>{t('w23.kioskEvents.col.status')}</Th>
                <Th>{t('w23.kioskEvents.col.reviewed')}</Th>
                <Th>
                  <span className="sr-only">{t('w23.kioskEvents.col.actions')}</span>
                </Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState message={t('w23.kioskEvents.empty')} />
                  </td>
                </tr>
              ) : null}
              {q.data.items.map((item) => (
                <Row key={item.idempotencyKey} item={item} siteName={siteName(item.siteId)} onReviewed={() => void qc.invalidateQueries({ queryKey: kioskEventsKey })} />
              ))}
            </tbody>
          </TableWrap>
          <Pager page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}
